import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, Zap, Factory, Gauge, Award, Cpu, Quote, Star } from "lucide-react";
import { fetchProducts, fetchTestimonials, fetchClients, fetchHomeHeroContent } from "../lib/api";
import ProductCard from "../components/ProductCard";
import InquiryDialog from "../components/InquiryDialog";
import HeroBackground from "../components/HeroBackground";
import { Counter, Reveal } from "../components/motion-primitives";
import { HERO, HOME_SECTIONS } from "../constants/testIds";

const PILLARS = [
  { icon: ShieldCheck, title: "Government-grade compliance", body: "ISO 9001, CE and BIS-certified builds. Documented URS-to-FAT for every project." },
  { icon: Zap, title: "Direct-drive engineering", body: "Servo-controlled architectures with sub-micron repeatability and 3-shift duty cycles." },
  { icon: Factory, title: "Built end-to-end in India", body: "In-house design, casting, machining, assembly and commissioning under one roof." },
  { icon: Gauge, title: "IIoT & OEE by default", body: "OPC-UA, MQTT and 21 CFR Part 11 ready SCADA on every machine we ship." },
  { icon: Award, title: "Trusted by PSUs", body: "Empanelled with Railways, Defense PSUs and India's leading pharma majors." },
  { icon: Cpu, title: "Lifetime engineering support", body: "Digital twin, remote diagnostics and dedicated engineer per installation." },
];

function HeroSkeleton() {
  return (
    <div className="md:col-span-4 hidden md:block">
      <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 animate-pulse">
        <div className="h-3 w-24 rounded-full bg-white/15" />
        <div className="mt-4 h-8 w-11/12 rounded-xl bg-white/15" />
        <div className="mt-3 h-8 w-9/12 rounded-xl bg-white/15" />
        <div className="mt-8 grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index}>
              <div className="h-3 w-20 rounded-full bg-white/10" />
              <div className="mt-2 h-4 w-16 rounded-full bg-white/15" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HeroStatsSkeleton() {
  return (
    <section className="section-x mx-auto max-w-[1440px] -mt-14 relative z-10">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-25px_rgba(10,37,64,0.25)] grid grid-cols-1 sm:grid-cols-3 overflow-hidden">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="p-8 md:p-10 border-b sm:border-b-0 sm:border-r last:border-r-0 border-slate-100 animate-pulse">
            <div className="h-12 w-24 rounded-xl bg-slate-200" />
            <div className="mt-4 h-4 w-32 rounded-full bg-slate-200" />
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialsSkeleton() {
  return (
    <section data-testid={HOME_SECTIONS.testimonials} className="section-x mx-auto max-w-[1440px] py-24">
      <div className="max-w-2xl">
        <div className="h-3 w-28 rounded-full bg-slate-200 animate-pulse" />
        <div className="mt-4 h-12 w-[min(100%,36rem)] rounded-2xl bg-slate-200 animate-pulse" />
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="relative h-full rounded-xl border border-slate-200 bg-white p-8 animate-pulse">
            <div className="h-8 w-8 rounded-full bg-slate-200" />
            <div className="mt-4 space-y-3">
              <div className="h-4 w-full rounded-full bg-slate-200" />
              <div className="h-4 w-11/12 rounded-full bg-slate-200" />
              <div className="h-4 w-9/12 rounded-full bg-slate-200" />
            </div>
            <div className="mt-6 flex gap-2">
              {Array.from({ length: 5 }).map((__, starIndex) => (
                <div key={starIndex} className="h-4 w-4 rounded-full bg-slate-200" />
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 space-y-2">
              <div className="h-4 w-32 rounded-full bg-slate-200" />
              <div className="h-3 w-40 rounded-full bg-slate-200" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clients, setClients] = useState([]);
  const [inqOpen, setInqOpen] = useState(false);
  const [homeHeroContent, setHomeHeroContent] = useState(null);
  const [homeHeroLoading, setHomeHeroLoading] = useState(true);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);

  const heroContent = homeHeroContent || {};

  const heroStats = [
    {
      end: Number(heroContent.machines_delivered),
      suffix: "+",
      label: "Machines delivered",
      visible: heroContent.machines_delivered !== "" && heroContent.machines_delivered !== null && heroContent.machines_delivered !== undefined,
    },
    {
      end: Number(heroContent.enterprise_clients),
      suffix: "+",
      label: "Enterprise clients",
      visible: heroContent.enterprise_clients !== "" && heroContent.enterprise_clients !== null && heroContent.enterprise_clients !== undefined,
    },
    {
      end: Number(heroContent.engineering_years),
      suffix: " yrs",
      label: "Engineering heritage",
      visible: heroContent.engineering_years !== "" && heroContent.engineering_years !== null && heroContent.engineering_years !== undefined,
    },
  ].filter((stat) => stat.visible && Number.isFinite(stat.end));

  const latestDeliveryFields = [
    { label: "Cycle time", value: heroContent.cycle_time },
    { label: "Uptime", value: heroContent.uptime },
    { label: "Weld quality", value: heroContent.weld_quality },
    { label: "Payback", value: heroContent.payback },
  ].filter((field) => String(field.value || "").trim().length > 0);

  const showLatestDeliveryCard =
    String(heroContent.latest_delivery_overline || "").trim().length > 0 ||
    String(heroContent.latest_delivery_title || "").trim().length > 0 ||
    latestDeliveryFields.length > 0;

  useEffect(() => {
    let active = true;

    setHomeHeroLoading(true);
    setTestimonialsLoading(true);

    fetchProducts({ featured: true, limit: 6 })
      .then((data) => {
        if (active) {
          setFeatured(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (active) {
          setFeatured([]);
        }
      });

    fetchClients()
      .then((data) => {
        if (active) {
          setClients(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (active) {
          setClients([]);
        }
      });

    fetchTestimonials()
      .then((data) => {
        if (active) {
          setTestimonials(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (active) {
          setTestimonials([]);
        }
      })
      .finally(() => {
        if (active) {
          setTestimonialsLoading(false);
        }
      });

    fetchHomeHeroContent()
      .then((data) => {
        if (active) {
          setHomeHeroContent(data || null);
        }
      })
      .catch(() => {
        if (active) {
          setHomeHeroContent(null);
        }
      })
      .finally(() => {
        if (active) {
          setHomeHeroLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <section className="relative bg-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0">
          <HeroBackground />
          <div className="absolute inset-0 bg-brand-navy/72" />
        </div>
        <div className="relative section-x mx-auto max-w-[1440px] pt-24 pb-28 md:pt-32 md:pb-36 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-200"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> Industrial Automation • Est. 2026
            </motion.div>

            <motion.h1
              data-testid={HERO.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-[76px] font-black leading-[1.02] tracking-tight text-balance"
            >
              Precision machines <br className="hidden md:block" />
              built for <span className="text-accent">critical industries.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-7 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed"
            >
              Buildwit Technology designs and manufactures industrial automated machinery for government organisations, defence, railways, pharma and heavy manufacturing. Engineered in India. Trusted by industry.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={HERO.ctaQuote}
                onClick={() => setInqOpen(true)}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-accent hover:bg-accent-hover text-white text-sm font-semibold shadow-[0_10px_30px_-10px_rgba(249,115,22,0.7)] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
              >
                Request Quote <ArrowRight className="h-4 w-4" />
              </button>
              <Link
                to="/products"
                data-testid={HERO.ctaExplore}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-white/20 hover:border-white/40 text-white text-sm font-semibold transition-colors duration-200"
              >
                Explore machinery <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          {homeHeroLoading ? (
            <HeroSkeleton />
          ) : showLatestDeliveryCard ? (
            <div className="md:col-span-4 hidden md:block">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
                className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6"
              >
                {String(heroContent.latest_delivery_overline || "").trim().length > 0 ? (
                  <div className="overline text-accent">{heroContent.latest_delivery_overline}</div>
                ) : null}
                {String(heroContent.latest_delivery_title || "").trim().length > 0 ? (
                  <div className="mt-3 font-display text-2xl font-bold leading-tight">{heroContent.latest_delivery_title}</div>
                ) : null}
                {latestDeliveryFields.length > 0 ? (
                  <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                    {latestDeliveryFields.map((field) => (
                      <div key={field.label}>
                        <div className="text-slate-400 text-xs uppercase tracking-widest">{field.label}</div>
                        <div className="text-white font-semibold mt-1">{field.value}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </motion.div>
            </div>
          ) : null}
        </div>
      </section>

      {homeHeroLoading ? (
        <HeroStatsSkeleton />
      ) : heroStats.length > 0 ? (
        <section data-testid={HOME_SECTIONS.stats} className="section-x mx-auto max-w-[1440px] -mt-14 relative z-10">
          <div className={`rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-25px_rgba(10,37,64,0.25)] grid grid-cols-1 ${heroStats.length === 1 ? "sm:grid-cols-1" : heroStats.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
            {heroStats.map((s, i) => (
              <div key={i} className="p-8 md:p-10 border-b sm:border-b-0 sm:border-r last:border-r-0 border-slate-100">
                <div className="font-display text-4xl md:text-5xl font-black text-brand-navy tracking-tight">
                  <Counter end={s.end} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-sm text-slate-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="section-x mx-auto max-w-[1440px] py-24 grid md:grid-cols-12 gap-12 items-start">
        <Reveal className="md:col-span-5">
          <div className="overline text-accent">About us</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy leading-tight tracking-tight">Engineering excellence, delivered at industrial scale.</h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-6 md:col-start-7">
          <p className="text-slate-600 text-lg leading-relaxed">
            From CNC machining centres and packaging lines to fully-automated robotic cells and process reactors — every ShivShakti machine is engineered in-house, tested to industrial standards and commissioned by our own engineers.
          </p>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            We have supplied to Indian Railways, Defense PSUs, leading pharma majors and multinational OEMs — and support each installation with lifetime spares and remote diagnostics.
          </p>
          <Link to="/about" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">Read our story <ArrowUpRight className="h-4 w-4" /></Link>
        </Reveal>
      </section>

      <section data-testid={HOME_SECTIONS.whyus} className="bg-slate-50 border-y border-slate-200">
        <div className="section-x mx-auto max-w-[1440px] py-24">
          <div className="max-w-2xl">
            <div className="overline text-accent">Why Buildwit</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight">Six pillars that keep our machines running for decades.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="relative h-full rounded-xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(10,37,64,0.25)] transition-[transform,box-shadow] duration-300">
                  <span className="corner-mark tl" /> <span className="corner-mark br" />
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-brand-navy text-white">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-brand-navy tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid={HOME_SECTIONS.featured} className="section-x mx-auto max-w-[1440px] py-24">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-accent">Featured machinery</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight">Signature products, shipping now.</h2>
          </div>
          <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-accent transition-colors duration-200">
            View full catalogue <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      <section data-testid={HOME_SECTIONS.clients} className="bg-brand-navy text-white py-16 border-y border-white/5">
        <div className="section-x mx-auto max-w-[1440px] mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-accent">Trusted by</div>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-extrabold tracking-tight">Government PSUs and industry leaders.</h3>
          </div>
          <div className="text-sm text-slate-400 max-w-md">Empanelled vendors for Indian Railways, TATA and multiple state-run enterprises.</div>
        </div>
        <Marquee gradient gradientColor="#0A2540" gradientWidth={80} speed={40} pauseOnHover>
          {clients.filter((c) => c?.active !== false).map((c) => (
            <div key={c.id || c.name} className="mx-4 w-44 rounded-xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
              <div className="h-16 rounded-lg bg-white/95 p-2 shadow-inner">
                {c.logo ? (
                  <img src={c.logo} alt={`${c.name} logo`} className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-display text-lg font-bold tracking-tight text-brand-navy">
                    {c.name}
                  </div>
                )}
              </div>
              <p className="mt-3 truncate text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
                {c.name}
              </p>
              <p className="mt-1 truncate text-center text-[11px] text-slate-400">
                {c.industry || "Industrial Partner"}
              </p>
            </div>
          ))}
        </Marquee>
      </section>

      {testimonialsLoading ? (
        <TestimonialsSkeleton />
      ) : testimonials.length > 0 ? (
        <section data-testid={HOME_SECTIONS.testimonials} className="section-x mx-auto max-w-[1440px] py-24">
          <div className="max-w-2xl">
            <div className="overline text-accent">Voices of trust</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight">Enterprise teams that stayed with us.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div className="relative h-full rounded-xl border border-slate-200 bg-white p-8">
                  <Quote className="h-8 w-8 text-accent/70" />
                  <p className="mt-4 text-slate-700 text-base leading-relaxed">"{t.quote}"</p>
                  <div className="mt-6 flex items-center gap-1 text-accent">
                    {Array.from({ length: t.rating }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="font-semibold text-brand-navy">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.designation}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      ) : null}

      <section data-testid={HOME_SECTIONS.inquiryCta} className="section-x mx-auto max-w-[1440px] pb-24">
        <div className="relative overflow-hidden rounded-2xl bg-brand-navy text-white p-10 md:p-16">
          <div className="absolute inset-0 hero-grid opacity-60" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="overline text-accent">Start a project</div>
              <h3 className="mt-3 font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">Have a specification? We&apos;ll turn it into a machine.</h3>
              <p className="mt-4 text-slate-300 text-base md:text-lg max-w-2xl">Share your requirements — throughput, footprint, compliance — and receive a technical proposal within one business day.</p>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <button
                onClick={() => setInqOpen(true)}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-accent hover:bg-accent-hover text-white text-sm font-semibold shadow-[0_10px_30px_-10px_rgba(249,115,22,0.7)] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
              >
                Request Quote <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <InquiryDialog open={inqOpen} onOpenChange={setInqOpen} />
    </>
  );
}
